import {
  Carrito,
  isItemCarritoGuarderia,
  calcularTotalItems,
  ItemCarrito,
  IItemCarrito,
  ItemCarritoGuarderia,
  ItemCarritoHotel,
  isItemCarritoHotel,
  ItemCarritoSpa,
  isItemCarritoSpa,
  NuevoCarritoRequest,
  NuevosItemsCarritoDeMascota,
  NuevoItemCarritoGuarderia,
  itemCarritoGuarderiaToNuevoItem,
  itemCarritoHotelToNuevoItem,
  NuevoItemCarritoHotel,
  NuevoItemCarritoSpa,
  itemCarritoSpaToNuevoItem,
} from "../../../services/carritoServicios";

export type Model = {
  id: string;
  propietario?: {
    id: string;
    nombreCompleto: string;
    documento: string;
  };
  itemsPorIdMascota: Record<string, Array<ItemCarrito>>;
  total: number;
  descuento: number;
  mascotasPorId: Record<string, IItemCarrito["mascota"]>;
  estaSucio: boolean;
};

export const buildModel = (carrito: Carrito): Model => {
  let itemsPorIdMascota: Model["itemsPorIdMascota"] = {};
  let mascotasPorId: Model["mascotasPorId"] = {};
  carrito.items.forEach((item) => {
    const itemsMascotaExistentes = itemsPorIdMascota[item.mascota.id];
    if (!itemsMascotaExistentes) {
      itemsPorIdMascota[item.mascota.id] = Array.of(item);
      mascotasPorId[item.mascota.id] = item.mascota;
    } else {
      itemsPorIdMascota[item.mascota.id].push(item);
    }
  });
  return {
    id: carrito.id,
    propietario: carrito.propietario,
    itemsPorIdMascota,
    mascotasPorId,
    descuento: carrito.descuento,
    total: calcularTotal(itemsPorIdMascota, carrito.descuento),
    estaSucio: false,
  };
};

export const calcularTotal = (
  itemsPorIdMascota: Model["itemsPorIdMascota"],
  descuento: number
): number => {
  if (!itemsPorIdMascota || Object.keys(itemsPorIdMascota).length === 0) {
    return 0;
  }
  const itemsCarrito: ItemCarrito[] = Object.entries(itemsPorIdMascota).flatMap(
    (entry) => entry[1]
  );

  return calcularTotalItems(itemsCarrito) - descuento;
};

export const recalcularModelConTotal = (model: Model): Model => {
  return {
    ...model,
    total: calcularTotal(model.itemsPorIdMascota, model.descuento),
    estaSucio: true,
  };
};

export type ItemCarritoHandlerPayload = {
  val: number;
  idMascota: string;
  idItem: string;
};

export type EliminarItemCarritoPayload = { idItem: string, idMascota: string };

export type ItemCarritoHandler = (payload: ItemCarritoHandlerPayload) => void;

// Handlers
export type GuarderiaHandlers = {
  onPrecioFinalPlanChange: ItemCarritoHandler;
  onPrecioFinalDiaIndividualChange: ItemCarritoHandler;
  onPrecioFinalTransporteIdaChange: ItemCarritoHandler;
  onPrecioFinalTransporteVueltaChange: ItemCarritoHandler;
  onPrecioFinalTransporteIdaVueltaChange: ItemCarritoHandler;
};

export type HotelHandlers = {
  onPrecioFinalTarifaChange: ItemCarritoHandler;
  onPrecioFinalNocheIndividualChange: ItemCarritoHandler;
  onPrecioFinalTransporteIdaChange: ItemCarritoHandler;
  onPrecioFinalTransporteVueltaChange: ItemCarritoHandler;
  onPrecioFinalTransporteIdaVueltaChange: ItemCarritoHandler;
};

export type SpaHandlers = {
  onPrecioFinalTarifaChange: ItemCarritoHandler;
};

export const eliminarItemHandler = (
  payload: EliminarItemCarritoPayload,
  model: Model
): Model => {
  let nuevoModel = { ...model };
  let itemsActualizados = nuevoModel.itemsPorIdMascota[payload.idMascota].filter(
    (itemCarrito) => itemCarrito.id !== payload.idItem
  );
  
  if(itemsActualizados.length === 0){
    // Eliminar asociacion de items con carrito si no no hay items
    let nuevosItemsPorMascotas = {...nuevoModel.itemsPorIdMascota};
    delete nuevosItemsPorMascotas[payload.idMascota];
    nuevoModel.itemsPorIdMascota = nuevosItemsPorMascotas;
  } else {
    nuevoModel.itemsPorIdMascota[payload.idMascota] = itemsActualizados;
  }
   
  return recalcularModelConTotal(nuevoModel);
};

export type CarritoHandlers = {
  guarderia: GuarderiaHandlers;
  hotel: HotelHandlers;
  spa: SpaHandlers;
  descuento: (val: number) => void;
  eliminarItem: (payload: EliminarItemCarritoPayload) => void;
};

// Guarderia model mutations

// Reemplazador de items de guarderia.
// Recibiendo un mapeador de items generico
// y encargado de actualizar la coleccion de items de guarderia
// Utilizado por los actualizadores de campos especificos de items de guarderia, como precio, etc.
export const reemplazarItemGuarderia = (
  model: Model,
  idMascota: string,
  idItem: string,
  itemMapper: (item: ItemCarritoGuarderia) => ItemCarritoGuarderia
): Model => {
  const items = model.itemsPorIdMascota[idMascota];
  const itemsActualizados: ItemCarrito[] = items.map((item) => {
    if (item.id === idItem && isItemCarritoGuarderia(item)) {
      return itemMapper(item);
    }
    return item;
  });
  let modelActualizado: Model = model;
  modelActualizado.itemsPorIdMascota[idMascota] = itemsActualizados;
  return recalcularModelConTotal(modelActualizado);
};

// Actualizador de precio final de plan de guarderia.
// Devolviendo el modelo del resumen de carrito ya actualizado.
export const reemplazarPrecioFinalPlanGuarderia = (
  model: Model,
  payload: ItemCarritoHandlerPayload
): Model => {
  const mapper = (item: ItemCarritoGuarderia) => {
    let itemActualizado = { ...item };
    itemActualizado.datosPlan!!.precioFinal = payload.val;
    return itemActualizado;
  };
  return reemplazarItemGuarderia(
    model,
    payload.idMascota,
    payload.idItem,
    mapper
  );
};

export const reemplazarPrecioFinalDiaIndividualGuarderia = (
  model: Model,
  payload: ItemCarritoHandlerPayload
): Model => {
  const mapper = (item: ItemCarritoGuarderia) => {
    let itemActualizado: ItemCarritoGuarderia = { ...item };
    itemActualizado.datosDiasIndividuales!!.precioFinal = payload.val;
    return itemActualizado;
  };
  return reemplazarItemGuarderia(
    model,
    payload.idMascota,
    payload.idItem,
    mapper
  );
};

export const reemplazarPrecioFinalTransporteIdaGuarderia = (
  model: Model,
  payload: ItemCarritoHandlerPayload
): Model => {
  const mapper = (item: ItemCarritoGuarderia) => {
    let itemActualizado: ItemCarritoGuarderia = { ...item };
    itemActualizado.datosTransporte!!.datosIda!!.precioFinal = payload.val;
    return itemActualizado;
  };
  return reemplazarItemGuarderia(
    model,
    payload.idMascota,
    payload.idItem,
    mapper
  );
};

export const reemplazarPrecioFinalTransporteVueltaGuarderia = (
  model: Model,
  payload: ItemCarritoHandlerPayload
): Model => {
  const mapper = (item: ItemCarritoGuarderia) => {
    let itemActualizado: ItemCarritoGuarderia = { ...item };
    itemActualizado.datosTransporte!!.datosVuelta!!.precioFinal = payload.val;
    return itemActualizado;
  };
  return reemplazarItemGuarderia(
    model,
    payload.idMascota,
    payload.idItem,
    mapper
  );
};

export const reemplazarPrecioFinalTransporteIdaVueltaGuarderia = (
  model: Model,
  payload: ItemCarritoHandlerPayload
): Model => {
  const mapper = (item: ItemCarritoGuarderia) => {
    let itemActualizado: ItemCarritoGuarderia = { ...item };
    itemActualizado.datosTransporte!!.datosIdaVuelta!!.precioFinal =
      payload.val;
    return itemActualizado;
  };
  return reemplazarItemGuarderia(
    model,
    payload.idMascota,
    payload.idItem,
    mapper
  );
};

// Hotel model mutations
export const reemplazarItemHotel = (
  model: Model,
  idMascota: string,
  idItem: string,
  itemMapper: (item: ItemCarritoHotel) => ItemCarritoHotel
): Model => {
  const items = model.itemsPorIdMascota[idMascota];
  const itemsActualizados: ItemCarrito[] = items.map((item) => {
    if (item.id === idItem && isItemCarritoHotel(item)) {
      return itemMapper(item);
    }
    return item;
  });
  let modelActualizado: Model = model;
  modelActualizado.itemsPorIdMascota[idMascota] = itemsActualizados;
  return recalcularModelConTotal(modelActualizado);
};

export const reemplazarPrecioFinalTarifaHotel = (
  model: Model,
  payload: ItemCarritoHandlerPayload
): Model => {
  const mapper = (item: ItemCarritoHotel) => {
    let itemActualizado = { ...item };
    itemActualizado.datosTarifa!!.precioFinal = payload.val;
    return itemActualizado;
  };
  return reemplazarItemHotel(model, payload.idMascota, payload.idItem, mapper);
};

export const reemplazarPrecioFinalNocheIndividualHotel = (
  model: Model,
  payload: ItemCarritoHandlerPayload
): Model => {
  const mapper = (item: ItemCarritoHotel) => {
    let itemActualizado: ItemCarritoHotel = { ...item };
    itemActualizado.datosNochesIndividuales!!.precioFinal = payload.val;
    return itemActualizado;
  };
  return reemplazarItemHotel(model, payload.idMascota, payload.idItem, mapper);
};

export const reemplazarPrecioFinalTransporteIdaHotel = (
  model: Model,
  payload: ItemCarritoHandlerPayload
): Model => {
  const mapper = (item: ItemCarritoHotel) => {
    let itemActualizado: ItemCarritoHotel = { ...item };
    itemActualizado.datosTransporte!!.datosIda!!.precioFinal = payload.val;
    return itemActualizado;
  };
  return reemplazarItemHotel(model, payload.idMascota, payload.idItem, mapper);
};

export const reemplazarPrecioFinalTransporteVueltaHotel = (
  model: Model,
  payload: ItemCarritoHandlerPayload
): Model => {
  const mapper = (item: ItemCarritoHotel) => {
    let itemActualizado: ItemCarritoHotel = { ...item };
    itemActualizado.datosTransporte!!.datosVuelta!!.precioFinal = payload.val;
    return itemActualizado;
  };
  return reemplazarItemHotel(model, payload.idMascota, payload.idItem, mapper);
};

export const reemplazarPrecioFinalTransporteIdaVueltaHotel = (
  model: Model,
  payload: ItemCarritoHandlerPayload
): Model => {
  const mapper = (item: ItemCarritoHotel) => {
    let itemActualizado: ItemCarritoHotel = { ...item };
    itemActualizado.datosTransporte!!.datosIdaVuelta!!.precioFinal =
      payload.val;
    return itemActualizado;
  };
  return reemplazarItemHotel(model, payload.idMascota, payload.idItem, mapper);
};

// Spa model mutations
export const reemplazarItemSpa = (
  model: Model,
  idMascota: string,
  idItem: string,
  itemMapper: (item: ItemCarritoSpa) => ItemCarritoSpa
): Model => {
  const items = model.itemsPorIdMascota[idMascota];
  const itemsActualizados: ItemCarrito[] = items.map((item) => {
    if (item.id === idItem && isItemCarritoSpa(item)) {
      return itemMapper(item);
    }
    return item;
  });
  let modelActualizado: Model = model;
  modelActualizado.itemsPorIdMascota[idMascota] = itemsActualizados;
  return recalcularModelConTotal(modelActualizado);
};

export const reemplazarPrecioFinalTarifaSpa = (
  model: Model,
  payload: ItemCarritoHandlerPayload
): Model => {
  const mapper = (item: ItemCarritoSpa) => {
    let itemActualizado: ItemCarritoSpa = { ...item };
    itemActualizado.precioFinal = payload.val;
    return itemActualizado;
  };
  return reemplazarItemSpa(model, payload.idMascota, payload.idItem, mapper);
};

// Reemplazo carrito remoto
export const buildNuevoCarritoRequest = (model: Model): NuevoCarritoRequest => {
  const itemsPorIdMascota = new Map<string, NuevosItemsCarritoDeMascota>();
  Object.entries(model.itemsPorIdMascota).forEach((itemsDeIdMascota) => {
    const idMascota: string = itemsDeIdMascota[0];
    const itemsCarrito: ItemCarrito[] = itemsDeIdMascota[1];
    let nuevosItemsGuarderia: NuevoItemCarritoGuarderia[] = [];
    let nuevosItemsHotel: NuevoItemCarritoHotel[] = [];
    let nuevosItemsSpa: NuevoItemCarritoSpa[] = [];

    itemsCarrito.forEach((itemCarrito) => {
      if (isItemCarritoGuarderia(itemCarrito)) {
        nuevosItemsGuarderia.push(itemCarritoGuarderiaToNuevoItem(itemCarrito));
      }
      if (isItemCarritoHotel(itemCarrito)) {
        nuevosItemsHotel.push(itemCarritoHotelToNuevoItem(itemCarrito));
      }
      if (isItemCarritoSpa(itemCarrito)) {
        nuevosItemsSpa.push(itemCarritoSpaToNuevoItem(itemCarrito));
      }

      const nuevosItemsDeMascota: NuevosItemsCarritoDeMascota = {
        itemsGuarderia: nuevosItemsGuarderia,
        itemsHotel: nuevosItemsHotel,
        itemsSpa: nuevosItemsSpa,
      };

      itemsPorIdMascota.set(idMascota, nuevosItemsDeMascota);
    });

    return {
      idPropietario: model.propietario!!.id,
      descuento: model.descuento,
      itemsPorIdMascota: itemsPorIdMascota,
    };
  });

  const req: NuevoCarritoRequest = {
    idPropietario: model.propietario!!.id,
    descuento: model.descuento,
    itemsPorIdMascota,
  };

  return req;
};

export const buildNuevoCarritoVacioRequest = (): NuevoCarritoRequest => {
  return {
    idPropietario: null,
    descuento: 0,
    itemsPorIdMascota: new Map<string, NuevosItemsCarritoDeMascota>(),
  };
};
